import React, { createContext, useContext, useState, useEffect } from 'react';

const CTISSchemaContext = createContext();

/**
 * CTISSchemaProvider: The central nervous system for EMA Blueprint data.
 * It handles the master index load and provides a utility to fetch 
 * specific template DNA (e.g., Form_IN.template.json).
 */
export const CTISSchemaProvider = ({ children }) => {
  const [screenIndex, setScreenIndex] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initial load of the Master Index from the public folder
  useEffect(() => {
    fetch('/data/EMA_standards/_screen_index.json', {
      headers: {
        'Accept': 'application/json'
      }
    })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}: Index not found at /public/data/...`);
        return res.json();
      })
      .then(data => {
        setScreenIndex(data);
        setLoading(false);
      })
      .catch(err => {
        console.error("CTISSchemaProvider: Index Load Error", err.message);
        setLoading(false); 
      });
  }, []);

  /**
   * loadTemplate: Fetches the specific JSON for a screen.
   * This is designed to fetch your .template.json files dynamically.
   */
  const loadTemplate = async (templateId) => {
    if (!screenIndex) {
        console.warn("CTISSchemaProvider: Attempted to load template before index was ready.");
        return null;
    }

    // Find the entry in your index that matches the requested screen_id
    const entry = screenIndex.screens.find(s => s.screen_id === templateId);
    if (!entry) {
        console.error(`CTISSchemaProvider: No index entry found for ${templateId}`);
        return null;
    }

    try {
      /**
       * SIZZLE: This fetches the filename provided in your index (e.g., "Form_IN.template.json").
       * Make sure your physical files are in: public/data/EMA_standards/templates/.
       */
      const response = await fetch(`/data/EMA_standards/templates/${entry.template_file}`);
      
      if (!response.ok) throw new Error(`HTTP ${response.status}: Template file ${entry.template_file} missing`);
      
      const data = await response.json();
      return data; // Returns the full JSON structure (metadata, items_flat, items_tree).
    } catch (err) {
      console.error("CTISSchemaProvider: Template Fetch Error", err.message);
      return null;
    }
  };

  return (
    <CTISSchemaContext.Provider value={{ screenIndex, loadTemplate, loading }}>
      {children}
    </CTISSchemaContext.Provider>
  );
};

export const useCTISSchema = () => {
  const context = useContext(CTISSchemaContext);
  if (!context) {
    throw new Error("useCTISSchema must be used within a CTISSchemaProvider");
  }
  return context;
};